import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { editionsAPI, eventsAPI, speakersAPI, sessionsAPI } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Calendar, MapPin, Users, Mic2, TrendingUp, ArrowRight } from 'lucide-react';

export const DashboardPage = () => {
  const [stats, setStats] = useState({
    editions: 0,
    events: 0,
    speakers: 0,
    sessions: 0,
  });
  const [recentEditions, setRecentEditions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [editions, events, speakers, sessions] = await Promise.all([
        editionsAPI.getAll(),
        eventsAPI.getAll(),
        speakersAPI.getAll(),
        sessionsAPI.getAll(),
      ]);

      setStats({
        editions: editions.data.length,
        events: events.data.length,
        speakers: speakers.data.length,
        sessions: sessions.data.length,
      });

      setRecentEditions(editions.data.slice(0, 3));
    } catch (error) {
      console.error('Error loading dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const statCards = [
    { label: 'Edizioni', value: stats.editions, icon: Calendar, href: '/admin/editions', color: 'text-primary' },
    { label: 'Eventi', value: stats.events, icon: MapPin, href: '/admin/events', color: 'text-secondary' },
    { label: 'Speakers', value: stats.speakers, icon: Users, href: '/admin/speakers', color: 'text-accent' },
    { label: 'Sessions', value: stats.sessions, icon: Mic2, href: '/admin/sessions', color: 'text-green-500' },
  ];

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      <div>
        <h1 className="font-heading text-3xl font-bold tracking-tight mb-2">Dashboard</h1>
        <p className="text-muted-foreground">
          Panoramica del contenuto SIOS
        </p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {statCards.map((stat) => {
          const Icon = stat.icon;
          return (
            <Link key={stat.label} to={stat.href} data-testid={`stat-${stat.label.toLowerCase()}`}>
              <Card className="bg-card border-white/5 hover:border-primary/30 transition-all hover:neon-shadow">
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                  <CardTitle className="text-sm font-medium text-muted-foreground">
                    {stat.label}
                  </CardTitle>
                  <Icon className={stat.color} size={20} />
                </CardHeader>
                <CardContent>
                  <p className="text-3xl font-heading font-bold">{stat.value}</p>
                </CardContent>
              </Card>
            </Link>
          );
        })}
      </div>

      {/* Recent Editions */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-heading text-xl font-semibold">Edizioni Recenti</h2>
          <Link to="/admin/editions" className="text-sm text-primary hover:underline flex items-center gap-1">
            Vedi tutte <ArrowRight size={14} />
          </Link>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {recentEditions.map((edition) => (
            <Card key={edition.id} className="bg-card border-white/5">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg">{edition.title}</CardTitle>
                  <span className={`text-xs px-2 py-1 rounded-full ${
                    edition.status === 'publish' 
                      ? 'bg-green-500/20 text-green-500' 
                      : 'bg-yellow-500/20 text-yellow-500'
                  }`}>
                    {edition.status === 'publish' ? 'Pubblicato' : 'Bozza'}
                  </span>
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground text-sm mb-2">{edition.year}</p>
                {edition.description && (
                  <p className="text-sm text-muted-foreground line-clamp-2">
                    {edition.description}
                  </p>
                )}
              </CardContent>
            </Card>
          ))}

          {recentEditions.length === 0 && !loading && (
            <div className="col-span-3 text-center py-12 glass rounded-xl">
              <Calendar className="mx-auto text-muted-foreground mb-4" size={32} />
              <p className="text-muted-foreground">Nessuna edizione ancora creata</p>
              <Link to="/admin/editions" className="text-primary hover:underline text-sm">
                Crea la prima edizione
              </Link>
            </div>
          )}
        </div>
      </div>

      {/* Quick Actions */}
      <div>
        <h2 className="font-heading text-xl font-semibold mb-4">Azioni Rapide</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Link to="/admin/editions">
            <Card className="bg-primary/10 border-primary/30 hover:bg-primary/20 transition-colors cursor-pointer">
              <CardContent className="pt-6 text-center">
                <Calendar className="mx-auto text-primary mb-2" size={24} />
                <p className="font-medium">Nuova Edizione</p>
              </CardContent>
            </Card>
          </Link>
          <Link to="/admin/events">
            <Card className="bg-secondary/10 border-secondary/30 hover:bg-secondary/20 transition-colors cursor-pointer">
              <CardContent className="pt-6 text-center">
                <MapPin className="mx-auto text-secondary mb-2" size={24} />
                <p className="font-medium">Nuovo Evento</p>
              </CardContent>
            </Card>
          </Link>
          <Link to="/admin/speakers">
            <Card className="bg-accent/10 border-accent/30 hover:bg-accent/20 transition-colors cursor-pointer">
              <CardContent className="pt-6 text-center">
                <Users className="mx-auto text-accent mb-2" size={24} />
                <p className="font-medium">Nuovo Speaker</p>
              </CardContent>
            </Card>
          </Link>
          <Link to="/admin/sessions">
            <Card className="bg-green-500/10 border-green-500/30 hover:bg-green-500/20 transition-colors cursor-pointer">
              <CardContent className="pt-6 text-center">
                <Mic2 className="mx-auto text-green-500 mb-2" size={24} />
                <p className="font-medium">Nuova Session</p>
              </CardContent>
            </Card>
          </Link>
        </div>
      </div>
    </div>
  );
};
